import React, { useEffect, useState } from 'react';
import axios from 'axios';
import config from 'constants/apiConfig';
import * as microsoftTeams from '@microsoft/teams-js';
import StylishNewSelect, {
  OptionType,
} from '../DesignSystems/New/StylishNewSelect';
import {
  selectGroup,
  selectGroups,
  selectIncident,
  selectIncidents,
  useAppSelector,
} from 'slices/commonSelectors';
import { useDispatch } from 'react-redux';

import {
  updateCurrentIncident,
  updateCurrentlySelectedGroup,
} from '../../actions/diceActions';

const TeamsTabConfig: React.FC = () => {
  const [theme, setTheme] = useState<string>('dark');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  const dispatch = useDispatch();

  const incident = useAppSelector(selectIncident);
  const incidents = useAppSelector(selectIncidents);
  const group = useAppSelector(selectGroup);
  const groups = useAppSelector(selectGroups);

  const [groupOptions, setGroupOptions] = useState<OptionType[]>([]);
  const [incidentOptions, setIncidentOptions] = useState<OptionType[]>([]);

  // Consent check and Teams init
  useEffect(() => {
    let tenantId: string | undefined;
    const checkConsentAndInit = async () => {
      setLoading(true);
      setErrorMessage(null);
      let payload = {};
      try {
        await microsoftTeams.app.initialize();
        const context = await microsoftTeams.app.getContext();
        tenantId = context.user?.tenant?.id;
        if (!tenantId) throw new Error('Missing tenantId');
        payload = { tenantId };
        await axios(config.apiGateway.teamsConsentCheck, {
          method: 'POST',
          data: payload,
        });
        // If we get here, permissions are fine, move on
        setErrorMessage(null);
        setLoading(false);
      } catch (error: any) {
        console.error('TeamsTabConfig API error:', error);
        const msg = error?.message?.toLowerCase() || '';
        if (
          error?.response?.status === 403 ||
          error?.response?.status === 400
        ) {
          setErrorMessage(
            `<strong>Admin Consent Required</strong><br/><br/>
            This app requires admin consent for organization-wide permissions.<br/><br/>
            <b>To proceed, an administrator must:</b><br/>
            1. <a href="https://admin.teams.microsoft.com/policies/manage-apps" target="_blank">Install PRATUS in your organization's app catalog</a><br/>
            2. Grant admin consent for all required permissions.<br/><br/>
            If you are not an admin, please contact your IT administrator with these instructions.<br/><br/>
            <b>Resources:</b><br/>
            - <a href="https://www.disastertech.com/hubfs/Client_Success/User_Guide.pdf" target="_blank">User Guide</a><br/>
            - <a href="https://www.disastertech.com/knowledge" target="_blank">Knowledge Base</a><br/>
            - <a href="https://www.disastertech.com/knowledge/kb-tickets/new" target="_blank">Submit a support ticket</a>`
          );
        } else if (
          msg.includes('consent') ||
          msg.includes('service principal') ||
          msg.includes('not found in the tenant app catalog')
        ) {
          setErrorMessage(
            `<strong>Admin Consent Required</strong><br/><br/>
            This app requires admin consent for organization-wide permissions.<br/><br/>
            <b>To proceed, an administrator must:</b><br/>
            1. <a href="https://admin.teams.microsoft.com/policies/manage-apps" target="_blank">Install PRATUS in your organization's app catalog</a><br/>
            2. Grant admin consent for all required permissions.<br/><br/>
            If you are not an admin, please contact your IT administrator with these instructions.<br/><br/>
            <b>Resources:</b><br/>
            - <a href="https://www.disastertech.com/hubfs/Client_Success/User_Guide.pdf" target="_blank">User Guide</a><br/>
            - <a href="https://www.disastertech.com/knowledge" target="_blank">Knowledge Base</a><br/>
            - <a href="https://www.disastertech.com/knowledge/kb-tickets/new" target="_blank">Submit a support ticket</a>`
          );
        } else {
          setErrorMessage(
            'An unexpected error occurred. Please try again or contact support.'
          );
        }
        setLoading(false);
      }
    };
    checkConsentAndInit();
  }, [retryCount]);

  useEffect(() => {
    setGroupOptions(
      groups
        .filter((group) => !group.is_closed)
        .map((group) => ({
          label: group.group_name,
          value: group.group_guid,
        }))
    );
  }, [groups]);

  useEffect(() => {
    setIncidentOptions(
      incidents
        .filter((incident) => incident.group_guid === group.group_guid)
        .map((incident) => ({
          label: incident.name,
          value: incident.id,
        }))
    );
  }, [incidents, group]);

  useEffect(() => {
    if (errorMessage || loading) return;
    try {
      const url = window.location.origin + `/iap/${incident.id}/settings`;

      microsoftTeams.pages.config.registerOnSaveHandler((saveEvent) => {
        microsoftTeams.pages.config.setConfig({
          websiteUrl: url,
          contentUrl: url,
          entityId: 'PratusTeamTab',
          suggestedDisplayName: 'PRATUS',
        });
        saveEvent.notifySuccess();
      });
      // Set validity state to true when the page loads as there are no user settings to configure on this page.
      microsoftTeams.pages.config.setValidityState(true);

      // Get the current theme
      microsoftTeams.app.getContext().then((context) => {
        if (context.app.theme === 'dark' || context.app.theme === 'contrast') {
          setTheme('dark');
          document.querySelectorAll('body').forEach((item) => {
            item.style.backgroundColor = 'transparent';
            item.style.color = 'white';
          });
        } else {
          setTheme('light');
          document.querySelectorAll('body').forEach((item) => {
            item.style.backgroundColor = 'transparent';
            item.style.color = 'black';
          });
        }
      });
    } catch (e) {
      console.warn('Teams Tab Initialization Error: ', e);
    }
  }, [incident, errorMessage, loading]);

  const organizationChangeHandler = (orgGuid: string) => {
    if (orgGuid !== group?.group_guid) {
      dispatch(
        updateCurrentlySelectedGroup(
          groups.find((group) => group.group_guid === orgGuid)
        )
      );
      const firstIncident = incidents.find(
        (incident) => incident.group_guid === orgGuid
      );
      dispatch(updateCurrentIncident(firstIncident));
    }
  };

  const incidentChangeHandler = (incidentId: string) => {
    if (incidentId !== incident?.id) {
      dispatch(
        updateCurrentIncident(
          incidents.find((incident) => incident.id === incidentId)
        )
      );
    }
  };

  if (loading) {
    return <div style={{ padding: 24 }}>Checking permissions...</div>;
  }
  if (errorMessage) {
    return (
      <div style={{ padding: 24, maxWidth: 600 }}>
        <div style={{ color: theme === 'dark' ? 'white' : 'black' }}>
          <div style={{ whiteSpace: 'pre-line', marginBottom: 24 }}>
            <strong>PRATUS Setup</strong>
            <br />
            <br />
            <span dangerouslySetInnerHTML={{ __html: errorMessage }} />
          </div>
          <button
            style={{
              padding: '8px 20px',
              background: '#0056b3',
              color: 'white',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 16,
            }}
            onClick={() => setRetryCount((c) => c + 1)}
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="section-select">
        <label
          className="form-label"
          style={{ color: theme === 'dark' ? 'white' : 'black' }}
        >
          Sponsor Organization
        </label>
        <StylishNewSelect
          classes={`secondary sml ${theme}`}
          options={groupOptions}
          value={
            group?.group_guid
              ? {
                  value: group.group_guid,
                  label: group.group_name,
                }
              : ''
          }
          placeholder="Select a Organization"
          onChange={(e) => {
            organizationChangeHandler(e.value);
          }}
        />
        <br />
        <label
          className="form-label"
          style={{ color: theme === 'dark' ? 'white' : 'black' }}
        >
          Incident Workspace
        </label>
        <StylishNewSelect
          classes={`secondary sml ${theme}`}
          options={incidentOptions}
          value={
            incident?.id
              ? {
                  value: incident.id,
                  label: incident.name,
                }
              : ''
          }
          placeholder="Select an Incident Workspace"
          onChange={(e) => incidentChangeHandler(e.value)}
        />
      </div>
      <br />
      <p className="teams-text">
        PRATUS - One solution for incident management and training/exercise
        planning.
      </p>
      <p className="teams-text">
        Please select an incident workspace and then click &quot;Save&quot;
        below to add PRATUS as a Tab.
      </p>
    </>
  );
};

export default TeamsTabConfig;
